package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertTriangle
import co.maytes.standupbot.dashboard.features.roster.LinkStateChip
import co.maytes.standupbot.dashboard.styles.HomeArt
import co.maytes.standupbot.dashboard.styles.HomeClasses
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.shared.dto.standup.LinkStateDto
import co.maytes.standupbot.shared.dto.standup.UnlinkedPeopleDto
import co.maytes.standupbot.shared.dto.standup.UnlinkedPersonDto
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap

object UnlinkedPeopleModel {
    const val ROWS_SHOWN = 5

    fun visible(result: UnlinkedPeopleDto?): Boolean = result != null && (result.people.isNotEmpty() || result.directoryError)

    fun title(result: UnlinkedPeopleDto): String =
        if (result.people.isEmpty()) "Could not check links" else "Unlinked people (${result.total})"

    fun explanation(result: UnlinkedPeopleDto): String =
        when {
            result.directoryError && result.people.isEmpty() -> "Linear did not answer, so link state could not be checked."
            else -> "These people have no line in the standup until their accounts are linked."
        }

    fun shown(result: UnlinkedPeopleDto): List<UnlinkedPersonDto> = result.people.take(ROWS_SHOWN)

    fun hiddenCount(result: UnlinkedPeopleDto): Int = (result.total - shown(result).size).coerceAtLeast(0)

    fun standupsLabel(person: UnlinkedPersonDto): String =
        when (person.standups.size) {
            0 -> ""
            1 -> "On ${person.standups.single().name}"
            else -> "On ${person.standups.first().name} and ${person.standups.size - 1} more"
        }

    fun fixPath(person: UnlinkedPersonDto): String =
        person.standups.firstOrNull()?.let { "/app/standups/${it.id}" } ?: "/app/standups"

    fun showsGithub(person: UnlinkedPersonDto): Boolean =
        person.github != LinkStateDto.LINKED && person.github != LinkStateDto.NOT_CONNECTED
}

@Composable
fun UnlinkedPeopleCard(
    result: UnlinkedPeopleDto,
    index: Int,
) {
    HmDiv(
        HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_12,
        modifier = Modifier.staggerIn(index),
    ) {
        HmCardHead(
            title = UnlinkedPeopleModel.title(result),
            tint = Palette.accent.cssRgb(),
            icon = { LucideAlertTriangle(size = HomeArt.ICON_PX) },
        )
        HmMuted(UnlinkedPeopleModel.explanation(result))
        HmDiv(modifier = Modifier.stack(Spacing.xxs)) {
            UnlinkedPeopleModel.shown(result).forEach { person ->
                HmLink(UnlinkedPeopleModel.fixPath(person), HomeClasses.RUN) {
                    Avatar(name = person.displayName, src = person.avatarUrl, sizePx = HomeArt.AVATAR_PX)
                    HmDiv(HomeClasses.ROW_TEXT) {
                        HmStrong(person.displayName)
                        HmMuted(UnlinkedPeopleModel.standupsLabel(person))
                    }
                    Row(
                        modifier = Modifier.gap(Spacing.xxs),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (person.linear != LinkStateDto.LINKED) LinkStateChip(provider = "Linear", state = person.linear)
                        if (UnlinkedPeopleModel.showsGithub(person)) LinkStateChip(provider = "GitHub", state = person.github)
                    }
                }
            }
        }
        val hidden = UnlinkedPeopleModel.hiddenCount(result)
        if (hidden > 0) HmMuted("+$hidden more")
    }
}
